import { useEffect, useRef, useState } from "react";
import { Markdown } from "../agent/Markdown";
import type { OutputDocument, StructuredField } from "../../../../shared/types";

interface OutputViewProps {
  document: OutputDocument;
  readAsMarkdown?: boolean;
  titleAlreadyShown?: string;
  emptyMessage?: string;
  editable?: boolean;
  isSaving?: boolean;
  onSave?: (document: OutputDocument) => Promise<void> | void;
}

function isBlankPrimitive(value: StructuredField["value"]): boolean {
  return value === null || (typeof value === "string" && value.trim().length === 0);
}

function fieldText(value: StructuredField["value"]): string {
  if (value === null) return "—";
  if (typeof value === "boolean") return value ? "True" : "False";
  return String(value);
}

function coerceEditedValue(original: StructuredField["value"], nextValue: string, render: StructuredField["render"]): StructuredField["value"] {
  if (render === "code" || render === "list" || render === "markdown") {
    return nextValue;
  }
  if (typeof original === "number") {
    const parsed = Number(nextValue);
    return Number.isFinite(parsed) ? parsed : original;
  }
  if (typeof original === "boolean") {
    if (nextValue.trim().toLowerCase() === "true") return true;
    if (nextValue.trim().toLowerCase() === "false") return false;
    return original;
  }
  if (original === null) {
    return nextValue.trim().length > 0 ? nextValue : null;
  }
  return nextValue;
}

function renderStructuredField(field: StructuredField) {
  const value = fieldText(field.value);

  if (field.render === "markdown") {
    return <Markdown content={value} />;
  }

  if (field.render === "code") {
    return (
      <pre className="overflow-auto whitespace-pre-wrap rounded-lg bg-slate-950 p-3 text-sm text-slate-50">
        {value}
      </pre>
    );
  }

  if (field.render === "list") {
    const items = value
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean);

    if (items.length === 0) {
      return <p className="text-sm text-slate-500">—</p>;
    }

    return (
      <ul className="space-y-2 text-sm text-slate-800">
        {items.map((item) => (
          <li key={`${field.key}:${item}`} className="flex gap-2">
            <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }

  return <p className="whitespace-pre-wrap text-sm text-slate-800">{value}</p>;
}

export function OutputView({
  document,
  readAsMarkdown = false,
  titleAlreadyShown,
  emptyMessage = "This output has no content to display.",
  editable = false,
  isSaving = false,
  onSave,
}: OutputViewProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [bodyDraft, setBodyDraft] = useState(document.format === "markdown" || document.format === "text" ? document.body : "");
  const [fieldDrafts, setFieldDrafts] = useState<Record<string, string>>({});
  const bodyDraftRef = useRef<HTMLTextAreaElement | null>(null);
  const fieldDraftRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});

  useEffect(() => {
    setIsEditing(false);
    setBodyDraft(document.format === "markdown" || document.format === "text" ? document.body : "");
    setFieldDrafts(
      document.format === "structured"
        ? Object.fromEntries(document.fields.map((field) => [field.key, fieldText(field.value)]))
        : {},
    );
  }, [document]);

  async function handleSave() {
    if (!onSave) return;
    if (document.format === "markdown" || document.format === "text") {
      const nextBody = bodyDraftRef.current?.value ?? bodyDraft;
      await onSave({ ...document, body: nextBody });
      setIsEditing(false);
      return;
    }
    if (document.format === "structured") {
      await onSave({
        ...document,
        fields: document.fields.map((field) => ({
          ...field,
          value: coerceEditedValue(
            field.value,
            fieldDraftRefs.current[field.key]?.value ?? fieldDrafts[field.key] ?? fieldText(field.value),
            field.render,
          ),
        })),
      });
      setIsEditing(false);
    }
  }

  const showEditControls = editable && document.format !== "unknown" && Boolean(onSave);

  if (document.format === "markdown") {
    return (
      <div className="space-y-3">
        {showEditControls ? (
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <button
                  type="button"
                  onClick={() => void handleSave()}
                  disabled={isSaving}
                  className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60"
                >
                  {isSaving ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setBodyDraft(document.body);
                  }}
                  disabled={isSaving}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700"
              >
                Edit draft
              </button>
            )}
          </div>
        ) : null}
        {isEditing ? (
          <textarea
            ref={bodyDraftRef}
            value={bodyDraft}
            onChange={(event) => setBodyDraft(event.target.value)}
            className="min-h-[220px] w-full rounded-lg border border-slate-300 bg-white p-3 text-sm text-slate-900 outline-none"
          />
        ) : document.body.trim()
          ? (
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <Markdown content={document.body} reading titleAlreadyShown={titleAlreadyShown} />
            </div>
          )
          : <p className="mt-3 text-sm text-slate-600">{emptyMessage}</p>}
      </div>
    );
  }

  if (document.format === "text") {
    return (
      <div className="space-y-3">
        {showEditControls ? (
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <button
                  type="button"
                  onClick={() => void handleSave()}
                  disabled={isSaving}
                  className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60"
                >
                  {isSaving ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setBodyDraft(document.body);
                  }}
                  disabled={isSaving}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700"
              >
                Edit draft
              </button>
            )}
          </div>
        ) : null}
        {isEditing ? (
          <textarea
            ref={bodyDraftRef}
            value={bodyDraft}
            onChange={(event) => setBodyDraft(event.target.value)}
            className="min-h-[220px] w-full rounded-lg border border-slate-300 bg-white p-3 font-mono text-sm text-slate-900 outline-none"
          />
        ) : document.body.trim()
          ? (
            readAsMarkdown ? <Markdown content={document.body} reading titleAlreadyShown={titleAlreadyShown} /> : (
            <pre className="mt-4 max-h-[70vh] overflow-auto whitespace-pre-wrap rounded-lg bg-slate-950 p-4 text-sm text-slate-50">
              {document.body}
            </pre>
            )
          )
          : <p className="mt-3 text-sm text-slate-600">{emptyMessage}</p>}
      </div>
    );
  }

  if (document.format === "structured") {
    const visibleFields = document.fields.filter((field) => !isBlankPrimitive(field.value));
    if (visibleFields.length === 0) {
      return <p className="mt-3 text-sm text-slate-600">{emptyMessage}</p>;
    }

    return (
      <div className="mt-4 space-y-4">
        {showEditControls ? (
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <button
                  type="button"
                  onClick={() => void handleSave()}
                  disabled={isSaving}
                  className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60"
                >
                  {isSaving ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setFieldDrafts(Object.fromEntries(document.fields.map((field) => [field.key, fieldText(field.value)])));
                  }}
                  disabled={isSaving}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700"
              >
                Edit draft
              </button>
            )}
          </div>
        ) : null}
        {document.title ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Structured title</p>
            <p className="mt-2 text-base font-semibold text-slate-900">{document.title}</p>
          </div>
        ) : null}
        <div className="grid gap-3">
          {visibleFields.map((field) => (
            <div key={field.key} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{field.label}</p>
              <div className="mt-2">
                {isEditing ? (
                  <textarea
                    ref={(node) => {
                      fieldDraftRefs.current[field.key] = node;
                    }}
                    value={fieldDrafts[field.key] ?? fieldText(field.value)}
                    onChange={(event) => setFieldDrafts((current) => ({ ...current, [field.key]: event.target.value }))}
                    className="min-h-[88px] w-full rounded-lg border border-slate-300 bg-white p-3 text-sm text-slate-900 outline-none"
                  />
                ) : renderStructuredField(field)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
      <p className="text-sm font-medium text-amber-900">This output uses an unrecognized legacy body format.</p>
      <pre className="mt-3 overflow-auto whitespace-pre-wrap rounded-lg bg-white p-3 text-xs text-slate-700">
        {JSON.stringify(document.raw, null, 2)}
      </pre>
    </div>
  );
}
